import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

import {
  getSettings,
  updateSettings,
} from "../../../../../../../services/settingsServices";

import {
  uploadSettingsLogoClub,
  toAbsolutePhotoUrl,
} from "../../../../../../../services/settingsPhotoServices";

import {
  ASSOCIATION_DEFAULT_LOGO,
  DEFAULT_ASSOCIATION_SETTINGS,
  calculateAssociationStats,
  extractLogoUrl,
  getSeasonOptions,
  normalizeSettingsPayload,
  unwrapSettings,
  validateAssociationSettings,
  validateImageFile,
  withBust,
} from "../utils/associationSettingsUtils";

export function useAssociationSettings() {
  const [settings, setSettings] = useState(DEFAULT_ASSOCIATION_SETTINGS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [logoDrawerOpen, setLogoDrawerOpen] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [imgVersion, setImgVersion] = useState(0);

  const fetchSettings = useCallback(async () => {
    try {
      setLoading(true);

      const data = await getSettings();
      const first = unwrapSettings(data);

      if (first) {
        setSettings({
          ...DEFAULT_ASSOCIATION_SETTINGS,
          ...first,
          primary_color: first?.primary_color || "#4f46e5",
          autorisation_renouvellement: !!first?.autorisation_renouvellement,
        });
      }

      setImgVersion((value) => value + 1);
    } catch (error) {
      console.error("Erreur chargement paramètres :", error);
      toast.error("Impossible de charger les paramètres du club.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const seasonOptions = useMemo(() => getSeasonOptions(), []);

  const stats = useMemo(() => {
    return calculateAssociationStats(settings);
  }, [settings]);

  const logoSrc = useMemo(() => {
    const absoluteLogo = toAbsolutePhotoUrl(
      settings.logo_club?.trim() ? settings.logo_club : ""
    );

    return withBust(absoluteLogo || ASSOCIATION_DEFAULT_LOGO, imgVersion);
  }, [settings.logo_club, imgVersion]);

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;

    setSettings((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));

    if (name === "logo_club") {
      setImgVersion((current) => current + 1);
    }
  };

  const saveSettings = async (event) => {
    event?.preventDefault?.();

    const payload = normalizeSettingsPayload(settings);
    const errorMessage = validateAssociationSettings(payload);

    if (errorMessage) {
      toast.error(errorMessage);
      return;
    }

    try {
      setSaving(true);

      await updateSettings(payload);

      setSettings(payload);
      toast.success("Paramètres du club mis à jour avec succès.");
    } catch (error) {
      console.error("Erreur sauvegarde paramètres :", error);
      toast.error(
        error?.response?.data?.message ||
          error?.response?.data ||
          "Erreur lors de la mise à jour des paramètres."
      );
    } finally {
      setSaving(false);
    }
  };

  const openLogoDrawer = () => {
    setUploadProgress(0);
    setLogoDrawerOpen(true);
  };

  const closeLogoDrawer = () => {
    if (uploadingLogo) return;

    setUploadProgress(0);
    setLogoDrawerOpen(false);
  };

  const handleUploadLogo = async ({ file, outputFormat = "webp" }) => {
    const fileError = validateImageFile(file);

    if (!file) {
      toast.error("Veuillez sélectionner une image.");
      return;
    }

    if (fileError) {
      toast.error(fileError);
      return;
    }

    try {
      setUploadingLogo(true);
      setUploadProgress(0);

      const uploaded = await uploadSettingsLogoClub(file, {
        format: outputFormat,
        onUploadProgress: (event) => {
          if (!event.total) return;
          setUploadProgress(Math.round((event.loaded * 100) / event.total));
        },
      });

      const newLogoUrl = extractLogoUrl(uploaded);

      if (!newLogoUrl) {
        toast.warning(
          "Logo envoyé, mais l’URL retournée par l’API est introuvable."
        );
      } else {
        setSettings((prev) => ({
          ...prev,
          logo_club: newLogoUrl,
        }));
      }

      setImgVersion((value) => value + 1);
      toast.success("Logo du club mis à jour.");

      setLogoDrawerOpen(false);
    } catch (error) {
      console.error("Erreur upload logo :", error);
      toast.error(
        error?.response?.data?.message ||
          error?.response?.data ||
          "Erreur lors de l’upload du logo."
      );
    } finally {
      setUploadingLogo(false);
      setUploadProgress(0);
    }
  };

  return {
    settings,
    loading,
    saving,

    stats,
    seasonOptions,
    logoSrc,

    handleChange,
    saveSettings,
    fetchSettings,

    logoDrawerOpen,
    uploadingLogo,
    uploadProgress,
    openLogoDrawer,
    closeLogoDrawer,
    handleUploadLogo,
  };
}